import React from "react";
import {Control,FieldArrayWithId,UseFormWatch,} from "react-hook-form";
import { useTranslation } from "react-i18next";
import {Clock,Plus,Trash2,} from "lucide-react";
import ToggleSwitch from "../ui/ToggleSwitch";
import TimeInput from "../ui/Timeinput";
import {DayOfWeek,OpeningHourPeriod,} from "../../types/modules/opening-hours.type";
import { OpeningHoursFormData } from "../../hooks/useOpeningHoursForm";


interface Props {
    control: Control<OpeningHoursFormData>;
    watch: UseFormWatch<OpeningHoursFormData>;
    fields: FieldArrayWithId< OpeningHoursFormData,"days","id">[];
    activeDays: Record<DayOfWeek,boolean>;
    toggleDay: (day: DayOfWeek) => void;

    addPeriod: (dayIndex: number) => void;
    removePeriod: (
        dayIndex: number,
        periodIndex: number
    ) => void;
    applied: boolean;
    handleApplyToggle: () => void;
}


    const WEEK_ORDER: DayOfWeek[] = [
    "mon",
    "tue",
    "wed",
    "thu",
    "fri",
    "sat",
    "sun",
    ];

const OpeningHoursInputs: React.FC<Props> =
    ({
        control,
        watch,
        fields,
        activeDays,
        toggleDay,
        addPeriod,
        removePeriod,
        applied,
        handleApplyToggle,
    }) => {
        const { t } =useTranslation();

        const sortedFields =
        [...fields].sort(
            (a, b) => {
            const aIndex = WEEK_ORDER.indexOf(a.day);
            const bIndex = WEEK_ORDER.indexOf(b.day);
            return (aIndex - bIndex);
            }
        );

    return (
        <div className="flex flex-col gap-6 mt-4">
            <div className="bg-gradient-to-r from-primary/5 to-transparent rounded-3xl border border-primary/10 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">

                <div className="p-2 bg-primary/10 rounded-xl">
                <Clock
                    size={20}
                    className="text-primary"
                />
                </div>

                <div className="flex flex-col">

                <span className="text-sm font-semibold text-Black">
                    {t(
                    "same_everyday"
                    )}
                </span>

                <span className="text-xs text-DarkGrey3">
                    {applied
                    ? t(
                        "applied"
                        )
                    : t(
                        "apply_to_all"
                        )}
                </span>

                </div>

            </div>
            <ToggleSwitch
                checked={applied}
                onChange={
                handleApplyToggle
                }
            />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {sortedFields.map(
                (field) => {
                const fieldIndex = fields.findIndex((item) =>item.id ===field.id);
                const isActive =activeDays[field.day];

                const periods: OpeningHourPeriod[] =
                    watch(`days.${fieldIndex}.periods`) ?? [];

                return (
                    <div
                    key={field.id}
                    className={`rounded-2xl border-2 transition-all duration-200 ${
                        isActive
                        ? "border-primary bg-white shadow-md"
                        : "border-LightGrey2 bg-LightGrey1/30 shadow-sm"
                    }`}
                    >

                    <button
                        type="button"
                        onClick={() =>
                        toggleDay(
                            field.day
                        )
                        }
                        className={`w-full px-5 py-3 rounded-t-xl font-semibold capitalize transition-all flex items-center justify-between ${
                        isActive
                            ? "bg-primary text-white"
                            : "bg-LightGrey2 text-DarkGrey3 hover:bg-LightGrey2"
                        }`}
                    >

                        <span>{t(field.day)}</span>

                        <span
                        className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                            isActive
                            ? "bg-white/20 text-white"
                            : "bg-white text-DarkGrey3"
                        }`}
                        >
                        {isActive
                            ? t(
                                "OPEN"
                            )
                            : t(
                                "CLOSED"
                            )}
                        </span>

                    </button>

                    {isActive && (
                        <div className="px-5 py-4 border-t border-LightGrey2 space-y-4">

                        {/* No periods */}
                        {periods.length ===
                            0 && (
                            <div className="text-sm text-gray-400 text-center py-2">
                            {t(
                                "no_opening_periods"
                            )}
                            </div>
                        )}

                        {periods.map(
                            (
                            _period: OpeningHourPeriod,
                            periodIndex: number
                            ) => (
                            <div
                                key={`${field.id}-${periodIndex}`}
                                className="rounded-xl bg-gray-50 border border-gray-100 p-3 space-y-3"
                            >

                                <div className="flex items-center justify-between">

                                <span className="text-xs font-semibold text-DarkGrey2">
                                    {t(
                                    "period"
                                    )}{" "}
                                    {periodIndex +
                                    1}
                                </span>

                                {periods.length >
                                    1 && (
                                    <button
                                    type="button"
                                    onClick={() =>
                                        removePeriod(
                                        fieldIndex,
                                        periodIndex
                                        )
                                    }
                                    className="text-danger hover:opacity-70 transition"
                                    >
                                    <Trash2
                                        size={
                                        16
                                        }
                                    />
                                    </button>
                                )}

                                </div>

                                <div className="flex flex-col gap-3">

                                <div className="flex items-center gap-3">

                                    <label className="text-xs font-semibold text-DarkGrey2 uppercase tracking-wider w-14 shrink-0">
                                    {t(
                                        "openTime"
                                    )}
                                    </label>

                                    <div className="flex-1">

                                    <TimeInput
                                        control={
                                        control
                                        }
                                        idx={
                                        fieldIndex
                                        }
                                        periodIdx={
                                        periodIndex
                                        }
                                        name="open"
                                    />

                                    </div>

                                </div>

                                <div className="flex items-center gap-3">

                                    <label className="text-xs font-semibold text-DarkGrey2 uppercase tracking-wider w-14 shrink-0">
                                    {t(
                                        "closeTime"
                                    )}
                                    </label>

                                    <div className="flex-1">

                                    <TimeInput
                                        control={
                                        control
                                        }
                                        idx={
                                        fieldIndex
                                        }
                                        periodIdx={
                                        periodIndex
                                        }
                                        name="close"
                                    />

                                    </div>

                                </div>

                                </div>

                            </div>
                            )
                        )}

                        <button
                            type="button"
                            onClick={() =>
                            addPeriod(
                                fieldIndex
                            )
                            }
                            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-primary/40 text-primary hover:bg-primary/5 transition text-sm font-medium"
                        >

                            <Plus
                            size={17}
                            />

                            {t(
                            "add_period"
                            )}

                        </button>

                        </div>
                    )}

                    </div>
                );
                }
            )}

            </div>

        </div>
    );
};
export default OpeningHoursInputs;